import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { T } from "../../app/tokens.js";
import { Tooltip } from "../../components/Tooltip.jsx";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { buildProjectUrl, buildProjectsUrl } from "../../services/buildUrl.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { useFxRates } from "../../data/useFxRates.js";
import { useCurrentProjectWeek } from "../../features/projects/weekly/ProjectWorkingUpdates.jsx";
import { enrichPortfolioProject } from "../../features/projects/portfolioRowModel.js";
import { ProjectsPortfolioList } from "../../features/projects/ProjectsPortfolioList.jsx";

const MOTION_INFO_TOOLTIP = "Active projects and their linked office lifecycle seat impact.";
const EMPTY_ROWS = [];

// Home "Portfolio in motion". Eligibility, Target ordering and the ten-row
// cap come from the model; rows are raw projects rendered by the same shared
// list (and Briefing peek) as the Projects landing. Read-only: no editing.
export function PortfolioMotionPane({
  model = null,
  loading = false,
  onNavigate,
  projectsEnabled = true,
} = {}) {
  const { open: showInfoTooltip, triggerProps } = useHoverIntent();
  const [peekProjectId, setPeekProjectId] = useState(null);
  const routerNavigate = useNavigate();
  const navigateRef = useRef(onNavigate ?? routerNavigate);
  navigateRef.current = onNavigate ?? routerNavigate;

  const sensitivity = useSensitivityPolicy();
  const canSeeBudget = sensitivity.canSee("projects.budget");
  const { rates } = useFxRates();
  const fxRatesKey = rates ? JSON.stringify(rates) : "";
  const fxRatesRef = useRef(rates);
  fxRatesRef.current = rates;

  // One shared current-week read per mounted Home pane.
  const [weekState, retryWeek] = useCurrentProjectWeek("home-portfolio-motion", 0);

  const rawRows = Array.isArray(model?.rows) ? model.rows : EMPTY_ROWS;
  const rows = useMemo(() => {
    const enrichRates = fxRatesKey ? fxRatesRef.current : null;
    return rawRows.map((project) => enrichPortfolioProject(project, { rates: enrichRates, canSeeBudget }));
  }, [rawRows, fxRatesKey, canSeeBudget]);

  // Drop a stale peek if its project leaves the model.
  useEffect(() => {
    if (peekProjectId != null && !rows.some((r) => r.id === peekProjectId)) setPeekProjectId(null);
  }, [rows, peekProjectId]);

  const handleTogglePeek = useCallback((id) => {
    setPeekProjectId((current) => (current === id ? null : id));
  }, []);
  const handleOpenFull = useCallback((id) => {
    if (!projectsEnabled) return;
    navigateRef.current(buildProjectUrl(id));
  }, [projectsEnabled]);
  const openProjects = useCallback(() => {
    if (!projectsEnabled) return;
    navigateRef.current(buildProjectsUrl());
  }, [projectsEnabled]);

  useEffect(() => {
    if (peekProjectId == null) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPeekProjectId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [peekProjectId]);

  return (
    <section data-testid="v2-section-portfolio-motion" style={{
      background: "var(--v2-card)", border: "1px solid var(--v2-line)",
      borderRadius: 16, boxShadow: "var(--v2-shadow-card)",
      padding: "16px 20px", marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsMd, fontWeight: T.fwBold }}>
          <span style={{ display: "inline-block", width: 3, height: 15, borderRadius: 2, background: "var(--v2-accent)" }} />
          Portfolio in motion
          <span
            data-testid="portfolio-motion-info-trigger"
            style={{ position: "relative", display: "inline-flex", color: "var(--v2-mid)", cursor: "help" }}
            {...triggerProps}
            tabIndex={0}
          >
            <Info size={14} />
            {showInfoTooltip && (
              <Tooltip
                data-testid="portfolio-motion-info-tooltip"
                style={{ top: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", whiteSpace: "normal", width: 260, maxWidth: 260, zIndex: 20 }}
              >
                {MOTION_INFO_TOOLTIP}
              </Tooltip>
            )}
          </span>
        </div>
      </div>

      {loading ? (
        <div data-testid="portfolio-motion-loading" style={{ minHeight: 90, display: "grid", placeItems: "center", color: "var(--v2-mid)", fontSize: T.fsSm5 }}>
          Loading portfolio motion…
        </div>
      ) : !model || model.projectCount === 0 ? (
        <div data-testid={model?.hasDegradedData ? "portfolio-motion-degraded" : "portfolio-motion-empty"} style={{ minHeight: 90, display: "grid", placeItems: "center", color: "var(--v2-mid)", fontSize: T.fsSm5, textAlign: "center" }}>
          {model?.hasDegradedData ? "Portfolio motion data is temporarily incomplete." : "No active projects in motion."}
        </div>
      ) : (
        <>
          {model.hasDegradedData && (
            <div data-testid="portfolio-motion-partial" style={{ color: "var(--v2-mid)", fontSize: T.fsSm5, padding: "4px 0 10px" }}>
              Portfolio motion data is temporarily incomplete.
            </div>
          )}
          <ProjectsPortfolioList
            testId="portfolio-motion-list"
            projects={rows}
            onTogglePeek={handleTogglePeek}
            onOpenFull={handleOpenFull}
            sticky={false}
            peekProjectId={peekProjectId}
            weekState={weekState}
            onRetryWeek={retryWeek}
            canSeeBudget={canSeeBudget}
            interactive={projectsEnabled}
            emptyMessage="No active projects in motion."
          />
          {projectsEnabled && <div style={{ marginTop: 12, textAlign: "right" }}>
            <button type="button" data-testid="portfolio-motion-view-all" onClick={openProjects} style={{ border: 0, background: "transparent", color: "var(--v2-accent)", fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: "pointer" }}>
              View all on the Projects board →
            </button>
          </div>}
        </>
      )}
    </section>
  );
}
